/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable max-lines */
import {
  AlarmTalkdownState,
  LocationAlarmUpdate,
  TalkdownStatus,
  TalkdownType,
} from '@hakimo-ui/hakimo/types';
import { FullScreenIcon } from '@hakimo-ui/hakimo/ui-elements';
import { Button, Modal } from '@hakimo-ui/shared/ui-base';
import {
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import { useState } from 'react';
import MiniAudioPlayer from '../../audio-recorder/mini-audio-player/MiniAudioPlayer';
import { SituationEditModal } from './SituationEditModal';

interface BasicColumn {
  id: string;
  name: string;
}
export const talkdownDetailsModalAllColumns: BasicColumn[] = [
  {
    id: 'rawAlarmId',
    name: 'Raw Alarm Id',
  },
  {
    id: 'rawAlarmAddedTime',
    name: 'Raw Alarm Added Time',
  },
  {
    id: 'status',
    name: 'Talkdown Status',
  },
  {
    id: 'ingestedTalkdown',
    name: 'Relayed Talkdown',
  },
  {
    id: 'talkdownTime',
    name: 'Talkdown Completed Time',
  },
  {
    id: 'comments',
    name: 'Comments',
  },
];

export const getColumnNode = (columns: BasicColumn[]) => {
  return columns.map((col) => (
    <div
      key={col.id}
      className="dark:text-ondark-text-1 font-bold text-gray-600"
    >
      {col.name}
    </div>
  ));
};

export function getTalkdownStatusIconNode(status: string) {
  let icon;
  switch (status) {
    case 'success':
    case TalkdownStatus.SUCCESS:
      icon = (
        <CheckCircleIcon className="h-5 w-5 text-green-400 dark:text-green-200" />
      );
      break;
    case 'warning':
      icon = (
        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 dark:text-yellow-200" />
      );
      break;
    case 'error':
    case TalkdownStatus.FAILED:
      icon = <XCircleIcon className="h-5 w-5 text-red-400 dark:text-red-300" />;
      break;
    default:
      icon = null;
  }
  return icon;
}

const getFormattedDate = (date?: string) => {
  if (!date) {
    return '-';
  }
  const dateObj = new Date(date);
  return dayjs(dateObj).format('DD/MM/YYYY, HH:mm:ss');
};

export function getNewAlarmEventDetailsNode(
  events: LocationAlarmUpdate[],
  talkdownsState: AlarmTalkdownState[],
  criticalAlarmIds?: Set<string>
) {
  // If criticalAlarmIds is provided (scan), only consider critical alarms
  // since talkdowns are only expected for critical alarms
  const relevantEvents = criticalAlarmIds
    ? events.filter(
        (ev) => ev.raw_alarm_id && criticalAlarmIds.has(ev.raw_alarm_id)
      )
    : events;
  const allEventsCount = relevantEvents.length;
  const noRawAlarmId = relevantEvents.every((ev) => !ev.raw_alarm_id);
  const automatedTalkdownState = talkdownsState.filter(
    (state) => state.talkdown_type === TalkdownType.AUTOMATED
  );
  if (noRawAlarmId || automatedTalkdownState.length === 0) {
    return null;
  }

  let eventTalkdownFailedCount = 0;
  for (let i = 0; i < relevantEvents.length; i++) {
    const rawAlarmId = relevantEvents[i].raw_alarm_id;
    const talkdownForAlarm = automatedTalkdownState.find(
      (talkdown) => talkdown.raw_alarm_id === rawAlarmId
    );
    if (talkdownForAlarm?.status !== TalkdownStatus.SUCCESS) {
      eventTalkdownFailedCount++;
    }
  }
  let message = '';
  let type = 'info';
  if (eventTalkdownFailedCount === 0) {
    message = `${
      allEventsCount > 1 ? 'All ' : ''
    }Automated talkdown were successfully transmitted`;
    type = 'success';
  } else if (eventTalkdownFailedCount === allEventsCount) {
    message = `${
      allEventsCount > 1 ? 'All ' : ''
    }Automated talkdown transmission failed`;
    type = 'error';
  } else {
    message = `${
      allEventsCount > 1 ? 'Some ' : ''
    }Automated talkdowns transmission failed`;
    type = 'warning';
  }

  return (
    <div className="flex items-center gap-2 text-xs">
      {message}
      {getTalkdownStatusIconNode(type)}
    </div>
  );
}

interface EventTalkdownDetails {
  rawAlarmId?: string;
  rawAlarmAddedTime: string;
  ingestedTalkdownAudio: string;
  customMessage: string;
  status: TalkdownStatus;
  sentTalkdownAudio: string;
  completedTimeUtc: string;
}

export function getNewAlarmEventDetailsModalContentNode(
  events: LocationAlarmUpdate[],
  talkdownsState: AlarmTalkdownState[],
  isSupportRole?: boolean,
  criticalAlarmIds?: Set<string>
) {
  const relevantEvents = criticalAlarmIds
    ? events.filter(
        (ev) => ev.raw_alarm_id && criticalAlarmIds.has(ev.raw_alarm_id)
      )
    : events;
  const noRawAlarmId = relevantEvents.every((ev) => !ev.raw_alarm_id);

  const automatedTalkdownState = talkdownsState.filter(
    (state) => state.talkdown_type === TalkdownType.AUTOMATED
  );
  if (noRawAlarmId || automatedTalkdownState.length === 0) {
    return null;
  }
  const eventTalkdownDetails: EventTalkdownDetails[] = [];
  relevantEvents.reduce((acc, ev) => {
    const rawAlarmId = ev.raw_alarm_id;
    const alarmTalkdownState = automatedTalkdownState.find(
      (talkdownState) => talkdownState.raw_alarm_id === rawAlarmId
    );
    if (alarmTalkdownState) {
      acc.push({
        rawAlarmId: alarmTalkdownState?.raw_alarm_id,
        rawAlarmAddedTime: ev.update_time,
        ingestedTalkdownAudio: alarmTalkdownState.ingested_talkdown_audio,
        customMessage: alarmTalkdownState.custom_message,
        status: alarmTalkdownState.status,
        sentTalkdownAudio: alarmTalkdownState.talkdown_audio,
        completedTimeUtc: alarmTalkdownState.talkdown_completed_utc,
      });
    }
    return acc;
  }, eventTalkdownDetails);

  const onClickRawAlarmId = (alarmId?: string) => () => {
    if (isSupportRole && alarmId) {
      const url = `/alarms/${alarmId}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const columns = talkdownDetailsModalAllColumns;

  return (
    <div className="text-sm">
      <div className="grid grid-cols-6 gap-2">{getColumnNode(columns)}</div>
      <div className="max-h-[70vh] overflow-auto">
        {eventTalkdownDetails.map((det) => (
          <div
            key={det.rawAlarmId}
            className="hover:bg-onlight-bg-2 dark:hover:bg-ondark-bg-2 grid grid-cols-6 gap-2 rounded py-6"
          >
            <div className="flex items-center gap-2">
              <span className="w-[8rem] overflow-hidden text-ellipsis whitespace-nowrap">
                {det.rawAlarmId}
              </span>
              {isSupportRole && (
                <Button
                  variant="icon"
                  onClick={onClickRawAlarmId(det.rawAlarmId)}
                >
                  <ArrowTopRightOnSquareIcon className="text-primary-400 h-3 w-3" />
                </Button>
              )}
            </div>
            <span>{getFormattedDate(det.rawAlarmAddedTime)}</span>
            <span className="flex gap-2">
              {getTalkdownStatusIconNode(det.status)}
              {det.status}
            </span>
            <span>
              {det.ingestedTalkdownAudio ? (
                <span className="flex">
                  <MiniAudioPlayer
                    src={det.ingestedTalkdownAudio}
                    isLoading={false}
                  />
                </span>
              ) : (
                'NA'
              )}
            </span>
            <span>{getFormattedDate(det.completedTimeUtc)}</span>
            <span>{det.customMessage || '-'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function getManualTalkdownDetailsNode(
  update: LocationAlarmUpdate,
  talkdownsStates: AlarmTalkdownState[]
) {
  let updateAudioUrl = update.audio_url;
  if (!updateAudioUrl) {
    return null;
  }
  updateAudioUrl = updateAudioUrl.split('?')[0];
  const manualTalkdownState = talkdownsStates.find(
    (state) =>
      state.talkdown_type === TalkdownType.MANUAL &&
      updateAudioUrl &&
      state.talkdown_audio.includes(updateAudioUrl)
  );

  const talkdownStatus = manualTalkdownState?.status;
  let message = '';
  let icon = null;
  switch (talkdownStatus) {
    case TalkdownStatus.SUCCESS:
      message = 'Talkdown transmitted successfully';
      icon = getTalkdownStatusIconNode('success');
      break;
    case TalkdownStatus.PENDING:
      message = 'Talkdown yet to be relayed';
      break;
    case TalkdownStatus.FAILED:
      message = 'Talkdown tranmission failed';
      icon = getTalkdownStatusIconNode('error');
      break;
  }

  return (
    <div className="flex items-center gap-2 text-xs">
      {message}
      {icon}
    </div>
  );
}

export function getManualTalkdownModalContentNode(
  update: LocationAlarmUpdate,
  talkdownsStates: AlarmTalkdownState[]
) {
  let updateAudioUrl = update.audio_url;
  if (!updateAudioUrl) {
    return null;
  }
  updateAudioUrl = updateAudioUrl.split('?')[0].split('audio')[1];
  if (!updateAudioUrl) {
    return;
  }
  const manualTalkdownState = talkdownsStates.find(
    (state) =>
      state.talkdown_type === TalkdownType.MANUAL &&
      updateAudioUrl &&
      state.talkdown_audio.includes(updateAudioUrl)
  );
  if (!manualTalkdownState) {
    return null;
  }

  const columns = talkdownDetailsModalAllColumns.filter(
    (col) => !['rawAlarmId', 'rawAlarmAddedTime'].includes(col.id)
  );

  return (
    <div className="grid grid-cols-4 gap-2 text-sm">
      {getColumnNode(columns)}
      <span className="flex gap-2">
        {getTalkdownStatusIconNode(manualTalkdownState.status)}
        {manualTalkdownState.status}
      </span>
      <span>
        {manualTalkdownState.ingested_talkdown_audio ? (
          <span className="flex">
            <MiniAudioPlayer
              src={manualTalkdownState.ingested_talkdown_audio}
              isLoading={false}
            />
          </span>
        ) : (
          'NA'
        )}
      </span>
      <span>
        {getFormattedDate(manualTalkdownState.talkdown_completed_utc)}
      </span>

      <span>{manualTalkdownState.custom_message || '-'}</span>
    </div>
  );
}

export function getSituationDetailsNode(update: LocationAlarmUpdate) {
  if (!update.situation && !update.situation_description) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1 text-xs">
      {update.situation && (
        <div className="flex items-center gap-2">
          <span className="dark:text-ondark-text-1 font-semibold text-gray-600">
            Situation:
          </span>
          <span className="dark:text-ondark-text-2 text-gray-700">
            {update.situation}
          </span>
        </div>
      )}
      {update.situation_description && (
        <div className="flex items-start gap-2">
          <span className="dark:text-ondark-text-1 font-semibold text-gray-600">
            Description:
          </span>
          <span className="dark:text-ondark-text-2 text-gray-700">
            {update.situation_description}
          </span>
        </div>
      )}
    </div>
  );
}

export function getSituationDetailsModalContentNode(
  update: LocationAlarmUpdate,
  alarmGroupId: string
) {
  if (!update.situation && !update.situation_description) {
    return null;
  }

  return <SituationEditModal update={update} alarmGroupId={alarmGroupId} />;
}

function SnapshotViewer({
  comment,
  urls,
}: {
  comment?: string;
  urls?: string[];
}) {
  const [expandIdx, setExpandIdx] = useState<number | null>(null);
  const trimmedComment = comment?.trim();
  const hasImages = urls && urls.length > 0;
  if (!trimmedComment && !hasImages) return null;

  const total = urls?.length ?? 0;

  return (
    <div className="mt-1 flex flex-col gap-1.5">
      {trimmedComment && (
        <p className="text-onlight-text-1 dark:text-ondark-text-1">
          Comment: {trimmedComment}
        </p>
      )}
      {hasImages && (
        <div className="flex flex-wrap gap-2">
          {urls.map((url, index) => (
            <div key={index} className="relative">
              <img
                src={url}
                alt={`Snapshot ${index + 1}`}
                className="h-14 w-20 cursor-pointer rounded border border-gray-300 object-cover dark:border-gray-600"
              />
              <Button
                variant="icon"
                className="absolute right-1 top-1 z-10 !p-0.5"
                onClick={() => setExpandIdx(index)}
              >
                <FullScreenIcon className="fill-onlight-text-1 dark:fill-ondark-text-1 h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
      <Modal
        open={expandIdx !== null}
        onClose={() => setExpandIdx(null)}
        title={
          expandIdx !== null ? `Snapshot ${expandIdx + 1} of ${total}` : ''
        }
        closable
      >
        <div className="w-full max-w-4xl p-2">
          <div className="relative flex items-center justify-center overflow-hidden rounded-xl bg-black">
            {expandIdx !== null && urls?.[expandIdx] && (
              <img
                src={urls[expandIdx]}
                alt="Expanded snapshot"
                className="max-h-screen max-w-full object-contain"
              />
            )}
            {total > 1 && expandIdx !== null && (
              <>
                <Button
                  variant="icon"
                  className="absolute left-2 top-1/2 z-10 -translate-y-1/2 bg-black/50 !p-1.5 hover:bg-black/75"
                  onClick={() => setExpandIdx((expandIdx - 1 + total) % total)}
                >
                  <ChevronLeftIcon className="h-5 w-5 text-white" />
                </Button>
                <Button
                  variant="icon"
                  className="absolute right-2 top-1/2 z-10 -translate-y-1/2 bg-black/50 !p-1.5 hover:bg-black/75"
                  onClick={() => setExpandIdx((expandIdx + 1) % total)}
                >
                  <ChevronRightIcon className="h-5 w-5 text-white" />
                </Button>
              </>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}

export function getSnapshotImagesNode(
  escalationComment?: string,
  s3Urls?: string[]
): JSX.Element | null {
  const hasImages = s3Urls && s3Urls.length > 0;
  if (!escalationComment?.trim() && !hasImages) return null;
  return <SnapshotViewer comment={escalationComment} urls={s3Urls} />;
}

export function getFormattedExportData(events: any[], queryData: any) {
  const findCorrespondingItem = (eventTime: string) => {
    return (
      queryData.items.find(
        (item: any) => new Date(item.update_time).toLocaleString() === eventTime
      ) || {}
    );
  };
  const formatDescription = (event: any) => {
    if (typeof event.description === 'object' && event.description !== null) {
      const userName = event.description.props?.userName || '-';
      const twilioCallingToName =
        event.description.props?.twilioCallingToName || '-';
      return userName !== '-' && twilioCallingToName !== '-'
        ? `${userName} made a call to ${twilioCallingToName}`
        : '-';
    }
    return event.description || '-';
  };

  const dataRows = events.map((event) => {
    const item = findCorrespondingItem(event.time);
    return {
      'Event Time': event.time || '-',
      'Operator Entered Time': event.eventTime || '-',
      'Event Description': formatDescription(event),
      'Update Type': item.update_type || '-',
      'Contact Name': item.twilio_calling_to_name || '-',
      'Recording URL': item.audio_url || '-',
    };
  });
  return dataRows;
}
